import React, { useState } from "react";
import ReactMarkdown from "react-markdown";

import Button, { BUTTON_STYLES } from "components/atoms/button";

import {
    ActionGroup,
    ActionRow,
    ErrorText,
    FormSectionHeader,
    FormSectionTitle,
    HelperText,
    HtmlReportBox,
    LogAccordionBody,
    LogAccordionCard,
    LogAccordionChevron,
    LogAccordionHeader,
    LogAccordionSummary,
    LogAccordionTitle,
    LogItem,
    LogMessage,
    LogMeta,
    LogStage,
    PanelCard,
    PanelHeader,
    PanelHeaderAside,
    PanelHeaderMain,
    PanelTitle,
    PhaseBadge,
    ProgressNote,
    ReportBox,
    ReportMainColumn,
    ResultContentGrid,
    SourceAside,
    SourceAsideEmpty,
    SourceAsideHeader,
    SourceAsideTitle,
    SourceActionRow,
    SourceBadge,
    SourceItem,
    SourceLinkText,
    SourceList,
    SourceTitleText,
} from "components/organisms/report/styled";

import remarkGfm from "remark-gfm";

const PHASE_LABEL_MAP = {
    idle: "대기",
    request_collected: "요청 확인",
    planning_generation: "계획 생성 중",
    planning_draft_ready: "계획 준비 완료",
    plan_confirmed: "분석 준비",
    researching: "리서치 진행 중",
    completed: "완료",
    cancelled: "취소됨",
    error: "오류",
};

export default function ResultStepSection({
    phase,
    error,
    resultState,
    sourceLabelMap,
    onGoBackPlanning,
    onCancelSession,
    onOpenResearch,
    onSaveResearch,
}) {
    const [isLogExpanded, setIsLogExpanded] = useState(false);
    const phaseKind = error ? "error" : phase === "completed" ? "done" : "progress";
    const phaseLabel = error ? "오류" : PHASE_LABEL_MAP[phase] || phase;
    const hasReportContent = !!(resultState.report || "").trim();
    const canOpenResearch = !!resultState.isPersisted && !!resultState.researchNo;
    const canSaveResearch =
        phase === "completed" && !error && hasReportContent && !canOpenResearch && !resultState.isSaving;
    const primaryButtonText = canOpenResearch
        ? "연구노트로 이동"
        : resultState.isSaving
          ? "저장 중..."
          : "연구노트로 저장";
    const logs = resultState.logs || [];
    const logSummary = logs.length > 0 ? `총 ${logs.length}개 로그` : "로그 없음";

    return (
        <PanelCard>
            <PanelHeader>
                <PanelHeaderMain>
                    <PanelTitle>리포트 생성</PanelTitle>
                    <HelperText>검색 계획에 따라 리포트와 참고문헌을 생성합니다.</HelperText>
                </PanelHeaderMain>
                <PanelHeaderAside>
                    <PhaseBadge kind={phaseKind}>{phaseLabel}</PhaseBadge>
                </PanelHeaderAside>
            </PanelHeader>

            {resultState.streaming && (
                <ProgressNote>
                    {resultState.currentStageMessage || "자료를 검색하고 리포트를 작성하고 있습니다."}
                </ProgressNote>
            )}

            {error && <ErrorText>{error}</ErrorText>}

            <ResultContentGrid>
                <ReportMainColumn>
                    <FormSectionHeader>
                        <FormSectionTitle>리포트</FormSectionTitle>
                    </FormSectionHeader>
                    {hasReportContent ? (
                        <HtmlReportBox>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{resultState.report}</ReactMarkdown>
                        </HtmlReportBox>
                    ) : (
                        <ReportBox>
                            {resultState.streaming ? "리서치를 진행 중입니다..." : "결과가 아직 없습니다."}
                        </ReportBox>
                    )}
                </ReportMainColumn>

                <SourceAside>
                    <SourceAsideHeader>
                        <SourceAsideTitle>참고문헌</SourceAsideTitle>
                        <HelperText>{resultState.sources.length}개</HelperText>
                    </SourceAsideHeader>
                    {resultState.sources.length > 0 ? (
                        <SourceList>
                            {resultState.sources.map((source) => (
                                <SourceItem
                                    key={`${source.index}-${source.url || source.title}`}
                                    href={source.url || undefined}
                                    target={source.url ? "_blank" : undefined}
                                    rel={source.url ? "noopener noreferrer" : undefined}
                                >
                                    <SourceTitleText>
                                        [{source.index}] {source.title || "Untitled"}
                                    </SourceTitleText>
                                    <SourceActionRow>
                                        <SourceBadge>
                                            {sourceLabelMap[source.source] || source.source || "Unknown"}
                                            {source.year ? ` (${source.year})` : ""}
                                        </SourceBadge>
                                        {source.url && <SourceLinkText>원문 보기</SourceLinkText>}
                                    </SourceActionRow>
                                </SourceItem>
                            ))}
                        </SourceList>
                    ) : (
                        <SourceAsideEmpty>리포트에 사용할 참고문헌을 수집하면 이곳에 표시됩니다.</SourceAsideEmpty>
                    )}
                </SourceAside>
            </ResultContentGrid>

            <LogAccordionCard>
                <LogAccordionHeader
                    type="button"
                    onClick={() => setIsLogExpanded((prev) => !prev)}
                    aria-expanded={isLogExpanded}
                    aria-controls="research-log-body"
                >
                    <div>
                        <LogAccordionTitle>검색 수행 로그</LogAccordionTitle>
                        <LogAccordionSummary>{logSummary}</LogAccordionSummary>
                    </div>
                    <LogAccordionChevron $expanded={isLogExpanded}>⌄</LogAccordionChevron>
                </LogAccordionHeader>
                {isLogExpanded && (
                    <LogAccordionBody id="research-log-body">
                        {logs.length > 0 ? (
                            logs.map((log, index) => (
                                <LogItem key={`${log.stage}-${index}`}>
                                    <LogStage>{log.stage || "log"}</LogStage>
                                    <LogMessage>{log.message || "-"}</LogMessage>
                                    {log.meta && Object.keys(log.meta).length > 0 && (
                                        <LogMeta>{JSON.stringify(log.meta, null, 2)}</LogMeta>
                                    )}
                                </LogItem>
                            ))
                        ) : (
                            <HelperText>아직 기록된 로그가 없습니다.</HelperText>
                        )}
                    </LogAccordionBody>
                )}
            </LogAccordionCard>

            <ActionRow>
                <ActionGroup>
                    <Button
                        {...BUTTON_STYLES.compactAction}
                        width={"140px"}
                        height={"44px"}
                        size={"small"}
                        buttonText={"이전 단계"}
                        onClick={onGoBackPlanning}
                    />
                    <Button
                        {...BUTTON_STYLES.compactAction}
                        width={"140px"}
                        height={"44px"}
                        size={"small"}
                        buttonText={"세션 초기화"}
                        onClick={onCancelSession}
                    />
                </ActionGroup>
                <ActionGroup>
                    <Button
                        onlyText
                        width={"140px"}
                        height={"44px"}
                        size={"small"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                        fontColor={"var(--color-White)"}
                        buttonText={primaryButtonText}
                        onClick={canOpenResearch ? onOpenResearch : onSaveResearch}
                        disabled={!canOpenResearch && !canSaveResearch}
                    />
                </ActionGroup>
            </ActionRow>
        </PanelCard>
    );
}
